import './PaginatedTable.scss';

import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { Reference } from 'rc-table';
import { SonarTable } from '@ui/SonarTable';
import { TableProps } from 'antd/es/table';
import { When } from 'react-if';

import { Button, Space } from 'antd';
import { ColumnType, ExpandableConfig, GetRowKey, SorterResult, SortOrder } from 'antd/es/table/interface';
import { ConfirmDeleteButton, ConfirmDeleteButtonProps } from '@ui/modals';
import { KeyPagination, keyPaginationDefaultQueryParams } from '@ui/KeyPagination';
import { KeyPaginationQueryParams, PaginatedResource, SonarV3SortDirection } from '@sonar-software/sonar-ts-types';
import React, { Key, useMemo, useRef } from 'react';
import { UpdateQueryParams, useCurrentUser } from '@hooks';

const sortDirectionMap: Record<NonNullable<SortOrder>, SonarV3SortDirection> = {
	ascend: SonarV3SortDirection.Ascending,
	descend: SonarV3SortDirection.Descending
};

export type PaginatedTableQueryParams<K extends Key = Key> = KeyPaginationQueryParams & {
	sortBy?: K;
	sortDirection?: SonarV3SortDirection;
};

export type PaginatedTableColumnDefinition<RecordType, K> = Omit<ColumnType<RecordType>, 'key'> & {
	key?: K | 'table-actions'
};

export interface PaginatedTableProps<T, S, K extends Key = Key> extends Partial<ConfirmDeleteButtonProps> {
	autohidePagination?: boolean;
	canDeleteRow?: (record: T) => boolean;
	canEditRow?: (record: T) => boolean;
	className?: string;
	columns: PaginatedTableColumnDefinition<T, K>[];
	defaultPageSize?: number;
	deletePrompt?: string | ((record: T) => string);
	expandable?: ExpandableConfig<T>;
	getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
	headerSize?: 'small' | 'normal';
	id?: string;
	loading?: boolean;
	onChange: UpdateQueryParams<PaginatedTableQueryParams<K>>;
	onDelete?: (record: T) => void;
	onEdit?: (record: T) => void;
	onRow?: TableProps<T>['onRow'];
	resource: PaginatedResource<T, S> | null | undefined;
	rowClassName?: TableProps<T>['rowClassName'];
	rowKey: GetRowKey<T>;
	scroll?: TableProps<T>['scroll'];
	showSizeChanger?: boolean;
	sortBy?: K;
	sortDirection?: SonarV3SortDirection;
	sticky?: boolean;
}

export const PaginatedTable = <T extends Record<string, unknown>, S = null, K extends Key = Key>({
	autohidePagination,
	canDeleteRow = () => true,
	canEditRow = () => true,
	className,
	columns,
	defaultPageSize = keyPaginationDefaultQueryParams.count as number,
	deletePrompt,
	expandable,
	getPopupContainer,
	headerSize = 'normal',
	id,
	loading,
	onChange,
	onDelete,
	onEdit,
	onRow,
	resource,
	rowClassName,
	rowKey,
	scroll,
	showSizeChanger,
	sortBy,
	sortDirection,
	sticky,
	...rest
}: PaginatedTableProps<T, S, K>) => {
	const { isReadOnly } = useCurrentUser();
	const scrollableListRef = useRef<Reference>(null);

	const managedColumns: PaginatedTableColumnDefinition<T, K>[] = useMemo(() => {
		let updatedColumns = columns;

		if ((onEdit || onDelete) && !isReadOnly) {
			updatedColumns = [
				...updatedColumns,
				{
					className: 'table-actions',
					key: 'table-actions',
					render: (_, record) =>
						<Space>
							<When condition={ canEditRow?.(record) }>
								{ onEdit &&
									<Button
										id={ `edit-record-button-${rowKey(record)}` }
										onClick={ () => onEdit(record) }
										type='link'>
										{ COPY_SPECIFIC.EDIT }
									</Button> }
							</When>
							<When condition={ canDeleteRow?.(record) }>
								{ onDelete && <ConfirmDeleteButton
									buttonId={ `delete-record-button-${rowKey(record)}` }
									getPopupContainer={ getPopupContainer }
									onConfirm={ () => onDelete(record) }
									placement='bottomRight'
									title={ typeof deletePrompt === 'function' ? deletePrompt(record) : deletePrompt }
									type='link'
									{ ...rest } /> }
							</When>
						</Space>,
					width: 100
				}
			];
		}

		if (sortBy && Number.isInteger(sortDirection)) {
			updatedColumns = updatedColumns.map((column) => {
				if (column.dataIndex === sortBy) {
					return {
						...column,
						sortOrder: sortDirection ? 'descend' : 'ascend'
					};
				}

				return column;
			});
		}

		return updatedColumns;
	}, [
		canEditRow,
		columns,
		onDelete,
		onEdit,
		isReadOnly,
		sortBy,
		sortDirection,
		canDeleteRow,
		rowKey,
		getPopupContainer,
		deletePrompt,
		rest
	]);

	return (
		<div
			className={ classNames('paginated-table', {
				'small-header': headerSize === 'small'
			}, className) }
			id={ id }>
			<SonarTable
				bordered={ false }
				columns={ managedColumns }
				dataSource={ resource?.results }
				expandable={ expandable }
				loading={ loading }
				onChange={ (_config, _filters, sorters: SorterResult<T>) => {
					onChange({
						sortBy: sorters.columnKey as K,
						sortDirection: sorters.order ? sortDirectionMap[ sorters.order ] : undefined
					});
				} }
				onRow={ onRow }
				pagination={ false }
				ref={ scrollableListRef }
				rowClassName={ rowClassName }
				rowKey={ rowKey }
				scroll={ {
					y: 'content',
					...scroll
				} }
				sticky={ sticky } />
			<KeyPagination
				autoHide={ autohidePagination }
				defaultPageSize={ defaultPageSize }
				listRef={ scrollableListRef }
				onChange={ onChange }
				resource={ resource }
				showSizeChanger={ showSizeChanger } />
		</div>
	);
};
